'use client';

import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

export const SidebarGroup = ({
  ref,
  className,
  ...props
}: ComponentProps<'div'> & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  );
};
SidebarGroup.displayName = 'SidebarGroup';

export const SidebarGroupLabel = ({
  ref,
  className,
  asChild = false,
  ...props
}: ComponentProps<'div'> & { asChild?: boolean } & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        'text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  );
};
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

export const SidebarGroupAction = ({
  ref,
  className,
  asChild = false,
  ...props
}: ComponentProps<'button'> & { asChild?: boolean } & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
};
SidebarGroupAction.displayName = 'SidebarGroupAction';

export const SidebarGroupContent = ({
  ref,
  className,
  ...props
}: ComponentProps<'div'> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn(
      'w-full text-sm',
      'group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center',
      className
    )}
    {...props}
  />
);
SidebarGroupContent.displayName = 'SidebarGroupContent';
